import React, { useState, useEffect, useReducer, useCallback, useMemo } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid,
  Tooltip,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import SearchIcon from "@material-ui/icons/Search";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import NewTicketModal from "../../components/NewTicketModal";
import formatPhone from "../../helpers/formatPhone";
import { fmtBRL, useFinanceiroStyles } from "./financeiroShared";

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const next = [...state];
    action.payload.forEach((item) => {
      const idx = next.findIndex((a) => a.id === item.id);
      if (idx !== -1) next[idx] = item;
      else next.push(item);
    });
    return next;
  }
  if (action.type === "REMOVE") return state.filter((a) => a.id !== action.payload);
  if (action.type === "RESET") return [];
  return state;
};

// ── Modal de confirmação de pagamento ──────────────────────────────────────

const ConfirmPaymentModal = ({ open, appointment, onClose, onConfirmed }) => {
  const [methods, setMethods] = useState([]);
  const [methodId, setMethodId] = useState("");
  const [paidAt, setPaidAt] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/payment-methods").then(({ data }) => {
      setMethods(data);
      const def = data.find((m) => m.isDefault);
      if (def) setMethodId(def.id);
    });
    setPaidAt(moment().format("YYYY-MM-DDTHH:mm"));
    setNotes("");
  }, [open]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await api.post(
        `/transactions/appointments/${appointment.id}/confirm-payment`,
        { paymentMethodId: methodId || undefined, paidAt, notes: notes || undefined }
      );
      toast.success("Pagamento confirmado!");
      onConfirmed(appointment);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirmar Pagamento</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          <strong>Agendamento #{appointment.id}</strong>
          {appointment.contact && ` — ${appointment.contact.name}`}
          {appointment.service && ` | ${appointment.service.name}`}
        </Typography>
        <Typography variant="h6" style={{ color: "#2e7d32", marginBottom: 16 }}>
          {fmtBRL(appointment.price)}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Forma de Pagamento</InputLabel>
              <Select value={methodId} onChange={(e) => setMethodId(e.target.value)}>
                <MenuItem value=""><em>Não informado</em></MenuItem>
                {methods.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Data/hora do pagamento"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Observações"
              fullWidth
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          variant="contained"
          style={{ backgroundColor: "#2e7d32", color: "#fff" }}
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
        >
          Confirmar Recebimento
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Painel principal ────────────────────────────────────────────────────────

const FILTERS = [
  ["all", "Todos"],
  ["late", "Atrasados"],
  ["nolaunch", "Sem lançamento"],
];

const ContasReceberPanel = ({ onSubtitle }) => {
  const classes = useFinanceiroStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [appointments, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalValue, setTotalValue] = useState(0);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [chatContact, setChatContact] = useState(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);

  const load = useCallback(async (page = 1, reset = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions/accounts-receivable", {
        params: { pageNumber: page }
      });
      if (reset) dispatch({ type: "RESET" });
      dispatch({ type: "LOAD", payload: data.appointments });
      setHasMore(data.hasMore);
      setTotalValue(data.totalValue);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    load(1, true);
  }, [load]);

  useEffect(() => {
    if (pageNumber > 1) load(pageNumber);
  }, [pageNumber, load]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const handleConfirmed = (appointment) => {
    dispatch({ type: "REMOVE", payload: appointment.id });
    setTotalValue((prev) => prev - Number(appointment?.price ?? 0));
  };

  const isLate = (appt) => moment(appt.startAt).isBefore(moment(), "day");

  const counts = useMemo(
    () => ({
      all: appointments.length,
      late: appointments.filter(isLate).length,
      nolaunch: appointments.filter((a) => !a.pendingTransaction).length,
    }),
    [appointments]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return appointments.filter((a) => {
      if (filter === "late" && !isLate(a)) return false;
      if (filter === "nolaunch" && a.pendingTransaction) return false;
      if (!term) return true;
      return [a.contact?.name, a.contact?.number, a.service?.name, a.user?.name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [appointments, search, filter]);

  useEffect(() => {
    onSubtitle(
      appointments.length === 0 && !loading
        ? "Nenhuma conta a receber"
        : `${appointments.length} atendimento${appointments.length === 1 ? "" : "s"} concluído${appointments.length === 1 ? "" : "s"} sem pagamento`
    );
  }, [onSubtitle, appointments.length, loading]);

  const openChat = (appt) => {
    if (!appt.contact?.id) {
      toast.warn("Este agendamento não tem contato vinculado.");
      return;
    }
    setChatContact(appt.contact);
    setTicketModalOpen(true);
  };

  const handleTicketClose = (ticket) => {
    setTicketModalOpen(false);
    if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
  };

  const launchPill = (appt) => (
    <Chip
      size="small"
      label={appt.pendingTransaction ? "Lançado" : "Sem lançamento"}
      className={`${classes.pill} ${appt.pendingTransaction ? classes.pillAmber : classes.pillGrey}`}
    />
  );

  const receiveButton = (appt) => (
    <Button
      size="small"
      className={classes.payButton}
      startIcon={<CheckIcon style={{ fontSize: 16 }} />}
      onClick={() => { setSelected(appt); setModalOpen(true); }}
    >
      Recebido
    </Button>
  );

  const chatButton = (appt) =>
    appt.contact?.id ? (
      <Tooltip title="Cobrar no WhatsApp">
        <IconButton size="small" className={classes.iconAction} aria-label="Cobrar no WhatsApp" onClick={() => openChat(appt)}>
          <WhatsAppIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null;

  const emptyText = appointments.length === 0
    ? "Nenhuma conta a receber. Todos os pagamentos estão em dia!"
    : "Nenhum resultado para esse filtro.";

  return (
    <div className={classes.root}>
      <ConfirmPaymentModal
        open={modalOpen}
        appointment={selected}
        onClose={() => { setModalOpen(false); setSelected(null); }}
        onConfirmed={handleConfirmed}
      />
      <NewTicketModal
        modalOpen={ticketModalOpen}
        initialContact={chatContact}
        onClose={handleTicketClose}
      />

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.callout}>
          <strong>Agendamentos concluídos que ainda não foram pagos.</strong> Clique em{" "}
          <strong>Recebido</strong> para confirmar o pagamento: o valor entra no Caixa e o item sai desta
          lista. <em>Lançado</em> = já existe um lançamento pendente; <em>Sem lançamento</em> = agendamento
          anterior ao módulo financeiro.
        </div>

        <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar cliente, serviço ou profissional"
            type="search"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ "aria-label": "Buscar contas a receber" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          <div className={`${classes.chipGroup} ${isMobile ? classes.chipGroupScroll : ""}`} role="group" aria-label="Situação">
            {FILTERS.map(([key, label]) => (
              <Chip
                key={key}
                clickable
                className={classes.filterChip}
                label={`${label} (${counts[key]})`}
                color={filter === key ? "primary" : "default"}
                variant={filter === key ? "default" : "outlined"}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              />
            ))}
          </div>
          {!isMobile && <span style={{ flex: 1 }} />}
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Total a receber:{" "}
            <span className={classes.income}>{fmtBRL(totalValue)}</span>
          </Typography>
        </div>

        <div className={classes.tableWrapper} onScroll={handleScroll}>
          {isMobile ? (
            <div>
              {!loading && visible.length === 0 && (
                <div className={classes.emptyState}>
                  <CheckCircleOutlineIcon className={classes.emptyIcon} />
                  <Typography variant="body2">{emptyText}</Typography>
                </div>
              )}
              {visible.map((appt) => (
                <div key={appt.id} className={`${classes.mobileRow} ${isLate(appt) ? classes.lateRow : ""}`}>
                  <div className={classes.mobileInfo}>
                    <div className={classes.mobileTitle}>{appt.contact?.name ?? "—"}</div>
                    <div className={classes.subtle}>
                      {[appt.service?.name, moment(appt.startAt).format("DD/MM")].filter(Boolean).join(" · ")}
                      {isLate(appt) && <span className={classes.lateText}> · atrasado</span>}
                    </div>
                  </div>
                  <div className={classes.mobileValue}>
                    <span className={classes.income}>{fmtBRL(appt.price)}</span>
                    {receiveButton(appt)}
                  </div>
                  {chatButton(appt)}
                </div>
              ))}
              {loading && appointments.length === 0 && (
                <Typography align="center" className={classes.muted} style={{ padding: 24 }}>
                  Carregando...
                </Typography>
              )}
            </div>
          ) : (
            <Table stickyHeader size="small" aria-label="contas a receber" style={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>Cliente</TableCell>
                  <TableCell className={classes.headCell}>Serviço</TableCell>
                  <TableCell className={classes.headCell}>Profissional</TableCell>
                  <TableCell className={classes.headCell}>Atendido em</TableCell>
                  <TableCell className={classes.headCell} align="right">Valor</TableCell>
                  <TableCell className={classes.headCell}>Situação</TableCell>
                  <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((appt) => {
                  const late = isLate(appt);
                  const days = moment().startOf("day").diff(moment(appt.startAt).startOf("day"), "days");
                  return (
                    <TableRow key={appt.id} className={`${classes.row} ${late ? classes.lateRow : ""}`}>
                      <TableCell style={{ paddingLeft: 20 }}>
                        <div style={{ fontWeight: 600 }}>{appt.contact?.name ?? "—"}</div>
                        {appt.contact?.number && (
                          <div className={classes.subtle}>{formatPhone(appt.contact.number)}</div>
                        )}
                      </TableCell>
                      <TableCell>{appt.service?.name ?? "—"}</TableCell>
                      <TableCell>{appt.user?.name ?? "—"}</TableCell>
                      <TableCell>
                        {moment(appt.startAt).format("DD/MM/YYYY HH:mm")}
                        {late && <div className={classes.lateText}>há {days} dia{days === 1 ? "" : "s"}</div>}
                      </TableCell>
                      <TableCell align="right"><span className={classes.income}>{fmtBRL(appt.price)}</span></TableCell>
                      <TableCell>{launchPill(appt)}</TableCell>
                      <TableCell align="right" style={{ paddingRight: 20 }}>
                        <div className={classes.actions}>
                          {chatButton(appt)}
                          {receiveButton(appt)}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && appointments.length === 0 && <TableRowSkeleton columns={7} />}
                {!loading && visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <div className={classes.emptyState}>
                        <CheckCircleOutlineIcon className={classes.emptyIcon} />
                        <Typography variant="body2">{emptyText}</Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {appointments.length > 0 && (
          <div className={classes.footer}>
            Mostrando {visible.length} de {appointments.length} atendimento{appointments.length === 1 ? "" : "s"}
            {hasMore ? " (role para carregar mais)" : ""}
          </div>
        )}
      </Paper>
    </div>
  );
};

export default ContasReceberPanel;
